import React, { useState, useEffect } from "react";
import { getMethodApiCall, postMethodApiCall } from "src/services/api";
import { toast } from "react-toastify";
import moment from "moment";
import QuestionItem from "../custom/QuestionItem";
import { TYPE } from "../../utils/constants";
import { isChoiceType } from "../../utils/helpers";

const QuestionnaireModal = ({ isOpen, attendanceId, traineeId, onComplete, date, onClose }) => {
  const [assessment_sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const isToday = moment(date).isSame(moment(), "day");

  useEffect(() => {
    if (isOpen) {
      setShowAnimation(true);
      setSections([]);
      setCurrentSection(0);
      setAnswers({});
      getMethodApiCall("/v1/api/trainee/questionnaire").then((res) => {
        if (res?.data?.success) setSections(res.data.data);
      });
    } else {
      setTimeout(() => setShowAnimation(false), 300);
    }
  }, [isOpen]);

  const handleAnswer = (questionId, value) =>
    setAnswers((prev) => ({ ...prev, [String(questionId)]: value }));

  const currentQuestions = assessment_sections[currentSection]?.assessment_master || [];

  const isCurrentSectionComplete = () =>
    currentQuestions.every((q) => {
      const val = answers[String(q.id)];

      if (q.type === TYPE.OPEN_TEXT) {
        return val !== undefined && val !== "" && val !== null;
      }
      if (q.type === TYPE.RATING) {
        return val !== undefined && val !== null;
      }

      const isMulti = q.type === TYPE.MULTIPLE_CHOICE;
      const selected = isMulti ? (val?.selected || []) : (val?.selected ?? val);

      if (isMulti) {
        if (!Array.isArray(selected) || selected.length === 0) return false;
      } else {
        if (selected === undefined || selected === null || selected === "") return false;
      }

      const otherOpt = q.assessment_options?.find((o) => o.is_other_option);
      if (otherOpt) {
        const otherSelected = isMulti
          ? Array.isArray(selected) && selected.map(String).includes(String(otherOpt.id))
          : String(selected) === String(otherOpt.id);
        if (otherSelected && !val?.otherText?.trim()) return false;
      }

      return true;
    });

  const handleSubmit = async () => {
    setSubmitting(true);
    const allQuestions = assessment_sections.flatMap((s) => s.assessment_master);
    const responses = [];

    Object.entries(answers).forEach(([questionnaire_id, value]) => {
      const question = allQuestions.find((q) => String(q.id) === questionnaire_id);

      if (question?.type === TYPE.OPEN_TEXT) {
        responses.push({ questionnaire_id, text_answer: value });
      } else if (question?.type === TYPE.RATING) {
        responses.push({ questionnaire_id, rating_value: Number(value) });
      } else if (isChoiceType(question?.type)) {
        const isMulti = question.type === TYPE.MULTIPLE_CHOICE;
        const selected = isMulti ? (value?.selected || []) : (value?.selected ?? value);
        const otherText = value?.otherText || "";

        if (isMulti) {
          (Array.isArray(selected) ? selected : []).forEach((opt_id) => {
            const opt = question?.assessment_options?.find((o) => String(o.id) === String(opt_id));
            if (opt?.is_other_option) {
              responses.push({ questionnaire_id, option_id: opt_id, text_answer: otherText });
            } else {
              responses.push({ questionnaire_id, option_id: opt_id });
            }
          });
        } else {
          const opt = question?.assessment_options?.find((o) => String(o.id) === String(selected));
          if (opt?.is_other_option) {
            responses.push({ questionnaire_id, option_id: selected, text_answer: otherText });
          } else {
            responses.push({ questionnaire_id, option_id: selected });
          }
        }
      }
    });

    const res = await postMethodApiCall("/v1/api/trainee/questionnaire/submit", {
      attendance_id: attendanceId,
      trainee_id: traineeId,
      responses,
    });

    setSubmitting(false);
    if (res?.data?.success) {
      toast.success("Questionnaire submitted.");
      onComplete();
    } else {
      toast.error(res?.data?.message || "Failed to submit. Please try again.");
    }
  };

  if (!showAnimation && !isOpen) return null;

  const isLastSection = currentSection === assessment_sections.length - 1;
  const sectionData = assessment_sections[currentSection];
  const complete = isCurrentSectionComplete();

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
        }`}
    >
      <div
        className={`bg-white rounded-xl shadow-xl w-[95%] sm:w-[85%] md:w-[70%] lg:w-[480px] max-h-[85vh] flex flex-col transform transition-transform duration-300 ${isOpen ? "scale-100" : "scale-90"
          }`}
      >
        {/* Header — title and date no longer run together on one line with
            a middot; each is legible at a glance on its own. */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-primary">Student Dairy</h2>
            <p className="text-xs text-gray-500 mt-1">  {moment(date).format("DD MMMM YYYY")}</p>
          </div>

          {assessment_sections.length > 0 && (
            <div className="mt-3 bg-primary/5 border border-primary/15 rounded-lg px-4 py-3">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-medium text-primary/70 uppercase tracking-wide">
                  Section {currentSection + 1} of {assessment_sections.length}
                </p>
                {assessment_sections.length > 1 && (
                  <div className="flex gap-1">
                    {assessment_sections.map((_, i) => (
                      <span
                        key={i}
                        className={`block rounded-full transition-all ${i === currentSection
                          ? "w-4 h-1.5 bg-primary"
                          : i < currentSection
                            ? "w-1.5 h-1.5 bg-primary/40"
                            : "w-1.5 h-1.5 bg-gray-300"
                          }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600">Before checking out, please reflect on this.</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">
                {sectionData.section}
                {sectionData.hindi_section && <span className="font-normal text-gray-500"> · {sectionData.hindi_section}</span>}
              </p>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
          {assessment_sections.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3" />
              <span className="text-sm text-gray-500">Loading questions...</span>
            </div>
          ) : (
            currentQuestions.map((q, idx) => (
              <QuestionItem
                key={q.id}
                index={idx + 1}
                question={q}
                value={answers[String(q.id)]}
                onChange={(val) => handleAnswer(q.id, val)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {assessment_sections.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-white rounded-b-xl flex-shrink-0">
            <button
              type="button"
              disabled={currentSection === 0}
              onClick={() => setCurrentSection((s) => s - 1)}
              className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Back
            </button>

            {!isLastSection ? (
              <button
                type="button"
                disabled={!complete}
                onClick={() => setCurrentSection((s) => s + 1)}
                className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 active:scale-95 transition-all"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                disabled={!complete || submitting}
                onClick={handleSubmit}
                className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 active:scale-95 transition-all flex items-center gap-2"
              >
                {submitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                {submitting
                  ? isToday
                    ? "Checking Out & Submitting..."
                    : "Submitting..."
                  : isToday
                    ? "Check Out & Submit"
                    : "Submit"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


export default QuestionnaireModal;